import { Alert, Snackbar, Stack } from '@mui/material';
import { DataGridPro, GridCellEditStopReasons, useGridApiRef } from '@mui/x-data-grid-pro';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FilterNetworkCommand from '../../../core/networkState/commands/FilterNetworkCommand';
import { InitNetworkCommand } from '../../../core/networkState/commands/InitNetworkCommand';
import LoadColumnsPreferenceCommand from '../../../core/networkState/commands/LoadColumnsPreferenceCommand';
import SaveColumnsPreferenceCommand from '../../../core/networkState/commands/SaveColumnsPreferenceCommand';
import {
    loadingNetworkSectionsQuery,
    networkSectionsColumnVisibilityQuery,
    networkSectionsFilterQuery,
    networkSectionsPaginationQuery,
    networkSectionsQuery,
} from '../../../core/networkState/queries';
import { paginationUpdated, setSelectedNetworkSections } from '../../../core/networkState/state';
import localeTexts from '../../../providers/translations/dataGridFilterTranslations';
import { useAppDispatch, useAppSelector } from '../../../types';
import { theme } from '../../legacy/style/theme';
import CustomPagination from '../../session/list-sessions/CustomPagination';
import CustomToolbar from '../../session/list-sessions/CustomToolbar';
import { gridStyle } from '../../style/datagrid';
import { EmptyNetworkList } from './EmptyNetworkList';
import { getDataGridColumns } from './getDataGridColumns';
import { NetworkHeader } from './NetworkHeader';

export const NetworkListPage: FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const networkSections = useAppSelector(networkSectionsQuery);
    const apiRef = useGridApiRef();
    const pagination = useAppSelector(networkSectionsPaginationQuery);
    const loadingState = useAppSelector(loadingNetworkSectionsQuery);
    const columnsVisibilityPref = useAppSelector(networkSectionsColumnVisibilityQuery);
    const filter = useAppSelector(networkSectionsFilterQuery);

    const [locallySelectedIds, setLocallySelectedIds] = useState<string[]>([]);
    const [panelElement, setPanelElement] = useState<HTMLElement | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    const columns = useMemo(() => getDataGridColumns(t, true), [t]);
    const isLoading = useMemo(() => loadingState || columns.length === 0, [loadingState, columns]);

    useEffect(() => {
        dispatch(InitNetworkCommand());
        dispatch(LoadColumnsPreferenceCommand());
    }, []);

    const handleColumnsVisibilityChange = (newPref: Record<string, boolean>) => {
        dispatch(SaveColumnsPreferenceCommand(newPref));
    };

    return (
        <Stack sx={{ width: '98%', height: '100%' }}>
            <NetworkHeader />
            <Stack
                sx={{
                    height: 'calc(100vh - 226px)',
                    padding: '10px 20px 0',
                    borderRadius: '6px',
                    boxShadow: theme.boxShadow.grey.thin,
                    backgroundColor: theme.color.white.main,
                }}
            >
                <DataGridPro
                    columns={columns}
                    apiRef={apiRef}
                    loading={isLoading}
                    rows={networkSections}
                    sx={gridStyle}
                    slots={{
                        noRowsOverlay: EmptyNetworkList,
                        toolbar: CustomToolbar,
                    }}
                    slotProps={{
                        panel: { anchorEl: panelElement },
                        toolbar: { setPanelElement },
                    }}
                    localeText={localeTexts}
                    filterModel={filter}
                    onFilterModelChange={(m) => dispatch(FilterNetworkCommand(m))}
                    onStateChange={({ filter: { filteredRowsLookup }, rowSelection }) => {
                        const selectedNetworkSectionsMatchingFilters =
                            Object.keys(filteredRowsLookup).length > 0
                                ? rowSelection.filter((id: string) => filteredRowsLookup[id])
                                : rowSelection;

                        dispatch(setSelectedNetworkSections(selectedNetworkSectionsMatchingFilters));
                    }}
                    rowSelectionModel={locallySelectedIds}
                    getRowId={(row) => row.id}
                    onRowSelectionModelChange={(m) => setLocallySelectedIds(m as string[])}
                    pagination
                    paginationModel={pagination}
                    onPaginationModelChange={(newPagination) => {
                        if (!isLoading) {
                            dispatch(paginationUpdated(newPagination));
                        }
                    }}
                    pageSizeOptions={[25, 50, 100, 500, 1000]}
                    columnVisibilityModel={columnsVisibilityPref}
                    onColumnVisibilityModelChange={handleColumnsVisibilityChange}
                    onCellEditStop={({ field, id, reason }, event) => {
                        if (reason === GridCellEditStopReasons.escapeKeyDown) {
                            event.defaultMuiPrevented = true;
                            apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
                        }
                    }}
                    checkboxSelection
                    hideFooter
                    hideFooterPagination
                />
            </Stack>
            <CustomPagination
                label="tronçon"
                pluralLabel="tronçons"
                apiRef={apiRef}
                onPaginationChange={(newPagination) => {
                    if (!isLoading) {
                        dispatch(paginationUpdated(newPagination));
                    }
                }}
            />
            {snackbarMessage && (
                <Snackbar
                    open
                    autoHideDuration={3000}
                    onClose={() => setSnackbarMessage(null)}
                    message={snackbarMessage}
                >
                    <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            )}
        </Stack>
    );
};
